import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

import styles from "./QuestionInput.module.css";
import { SpeechInput } from "./SpeechInput";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";

const SendIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
    </svg>
);

const StopCircleIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="14" cy="14" r="12.5" stroke="black" strokeWidth="2" fill="none" />
        <rect x="9" y="9" width="10" height="10" rx="1" fill="black" />
    </svg>
);

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    showSpeechInput?: boolean;
    onStop: () => void;
    isStreaming: boolean;
    isLoading: boolean;
}

export const QuestionInput = ({ onSend, onStop, disabled, placeholder, clearOnSend, initQuestion, showSpeechInput, isStreaming, isLoading }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const { loggedIn } = useContext(LoginContext);
    const { t } = useTranslation();
    const [isComposing, setIsComposing] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    // Auto-resize textarea to fit content
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 96) + "px";
    }, [question]);

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }
        onSend(question);
        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (isComposing) return;
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const disableRequiredAccessControl = requireLogin && !loggedIn;
    const sendQuestionDisabled = disabled || !question.trim() || disableRequiredAccessControl;
    const effectivePlaceholder = disableRequiredAccessControl ? "Please login to continue..." : placeholder;

    return (
        <div className={styles.questionInputContainer}>
            <textarea
                ref={textareaRef}
                className={styles.questionInputTextArea}
                disabled={disableRequiredAccessControl}
                placeholder={effectivePlaceholder}
                value={question}
                rows={1}
                onChange={ev => setQuestion(ev.target.value)}
                onKeyDown={onEnterPress}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
            />
            <div className={styles.questionInputButtonsContainer}>
                {isStreaming || isLoading ? (
                    <button className={styles.stopButton} onClick={onStop} aria-label={t("tooltips.stopStreaming")}>
                        <StopCircleIcon />
                    </button>
                ) : (
                    <button className={styles.sendButton} disabled={sendQuestionDisabled} onClick={sendQuestion} aria-label={t("tooltips.submitQuestion")}>
                        <SendIcon />
                    </button>
                )}
            </div>
            {showSpeechInput && <SpeechInput updateQuestion={setQuestion} />}
        </div>
    );
};
